import { FlowParamsContext } from '@/app/FlowInitializer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import { AppInputField, AppPromptsField, AppSelectField } from '@/form/FormUI';
import type { InitialRoomsNodeType, NodeFormProps } from '@/nodes';
import { allInfoTypesState, nodesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { listDataKeyValueOptions } from '@/util/dataKeyUtils';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    type DataKey,
    InitialRoomsDataSchema,
    type InitialRoomsDataType,
    type RoomType,
    RoomTypeLabels,
} from '@rock/types';
import { keyBy } from 'lodash';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import { type ComponentProps, type FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type FieldErrors, type UseFormReturn, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof InitialRoomsDataSchema>;

export const InitialRoomsForm: FC<NodeFormProps<InitialRoomsNodeType>> = ({
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, InitialRoomsDataType>({
        resolver: zodResolver(InitialRoomsDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: InitialRoomsDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What message are we showing the user?" />

                    <AppDataKeyField
                        label="Floors Data Key"
                        placeholder="numberOfStories"
                        control={form.control}
                        name="floorsDataKey"
                        description="The data key where the number of floors is stored in guide values."
                        mode="consume"
                        currentNodeId={node.id}
                    />

                    <AppDataKeyField
                        label="Has Basement Data Key"
                        placeholder="hasBasement"
                        control={form.control}
                        name="hasBasementDataKey"
                        description="The data key where the basement flag is stored in guide values."
                        mode="consume"
                        currentNodeId={node.id}
                    />

                    <DefaultRoomsField form={form} />

                    <PreselectRoomsField form={form} nodeId={node.id} />

                    <AppSelectField
                        label="Input Mode"
                        control={form.control}
                        name="inputMode"
                        options={['simple', 'name-and-floor']}
                        description="Simple mode shows basic room selection. Name-and-floor mode allows users to enter names and select floors for each room."
                    />

                    <AppInputField
                        label="Done Label"
                        placeholder="Continue"
                        control={form.control}
                        name="doneLabel"
                        description="The label on the button to continue."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

const ROOM_TYPE_OPTIONS = (Object.entries(RoomTypeLabels) as [RoomType, string][]).map(([value, label]) => ({
    value,
    label,
}));

type InitialRoomsFormReturn = UseFormReturn<FormInput, unknown, InitialRoomsDataType>;

const DefaultRoomsField: FC<{ form: InitialRoomsFormReturn }> = ({ form }) => {
    const defaults = useFieldArray({ control: form.control, name: 'defaultRooms' });

    return (
        <div className="flex flex-col gap-2">
            <Label>Default Rooms</Label>
            <FormDescription>
                Rooms the list starts with for a home that has no rooms yet. A room type the address lookup already
                counted keeps the lookup's count. Keep this step on every path, since the guide's finish creates these
                rooms even when the user never sees them.
            </FormDescription>
            {defaults.fields.map((defaultField, defaultIndex) => (
                <div key={defaultField.id} className="flex flex-row items-end gap-3">
                    <div className="flex-1">
                        <AppSelectField
                            label="Room"
                            smallLabel
                            placeholder="Select Room Type"
                            control={form.control}
                            name={`defaultRooms.${defaultIndex}.roomType`}
                            options={ROOM_TYPE_OPTIONS}
                        />
                    </div>
                    <AppInputField
                        label="Count"
                        smallLabel
                        inputType="number"
                        className="w-20"
                        control={form.control}
                        name={`defaultRooms.${defaultIndex}.count`}
                    />
                    <CircleX
                        onClick={() => defaults.remove(defaultIndex)}
                        className="mb-2 shrink-0 cursor-pointer fill-white hover:fill-red-300"
                    />
                </div>
            ))}
            <div className="text-center">
                <Button
                    type="button"
                    onClick={() => defaults.append({ roomType: 'bedroom', count: 1 })}
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer h-6 text-xs"
                >
                    <PlusCircleIcon /> Add Default Room
                </Button>
            </div>
        </div>
    );
};

const PreselectRoomsField: FC<{ form: InitialRoomsFormReturn; nodeId: string }> = ({ form, nodeId }) => {
    const rules = useFieldArray({ control: form.control, name: 'preselectRooms' });

    return (
        <div className="flex flex-col gap-2">
            <Label>Pre-Checked Rooms</Label>
            <FormDescription>
                Start a room checked when an earlier answer matches (e.g. an Attic when the attic answer is Finished or
                Unfinished). Applies on every run, and adds nothing when the home already has a room of that type.
            </FormDescription>
            {rules.fields.map((ruleField, ruleIndex) => (
                <div
                    key={ruleField.id}
                    className="relative group/rule flex flex-col gap-3 p-3 border-1 border-dashed border-gray-400"
                >
                    <CircleX
                        onClick={() => rules.remove(ruleIndex)}
                        className="absolute top-0 right-0 m-1 opacity-0 max-md:opacity-100 group-hover/rule:opacity-100 transition-opacity cursor-pointer translate-x-1/2 -translate-y-1/2 fill-white hover:fill-red-300"
                    />
                    <AppDataKeyField
                        label="Data Key"
                        smallLabel
                        placeholder="attic"
                        control={form.control}
                        name={`preselectRooms.${ruleIndex}.dataKey`}
                        mode="consume"
                        currentNodeId={nodeId}
                    />
                    <PreselectValuesField form={form} ruleIndex={ruleIndex} />
                    <AppSelectField
                        label="Room"
                        smallLabel
                        placeholder="Select Room Type"
                        control={form.control}
                        name={`preselectRooms.${ruleIndex}.roomType`}
                        options={ROOM_TYPE_OPTIONS}
                    />
                </div>
            ))}
            <div className="text-center">
                <Button
                    type="button"
                    onClick={() => rules.append({ dataKey: '' as DataKey, values: [], roomType: 'attic' })}
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer h-6 text-xs"
                >
                    <PlusCircleIcon /> Add Pre-Checked Room
                </Button>
            </div>
        </div>
    );
};

/**
 * Checkboxes for the values the rule's data key can hold, read off its producer; a comma-separated
 * input when the producer takes free text.
 */
const PreselectValuesField: FC<{ form: InitialRoomsFormReturn; ruleIndex: number }> = ({ form, ruleIndex }) => {
    const params = useContext(FlowParamsContext);
    const nodes = useAsyncAtomValue(nodesState(params));
    const infoTypes = useAsyncAtomValue(allInfoTypesState);
    const dataKey = useWatch({ control: form.control, name: `preselectRooms.${ruleIndex}.dataKey` });
    const options = useMemo(
        () => listDataKeyValueOptions(nodes ?? [], dataKey ?? '', keyBy(infoTypes ?? [], 'id')),
        [nodes, infoTypes, dataKey],
    );

    return (
        <FormField
            control={form.control}
            name={`preselectRooms.${ruleIndex}.values`}
            render={({ field }) => {
                const selected: string[] = field.value ?? [];
                const unknownSelected = selected.filter((value) => !options.some((o) => o.value === value));
                return (
                    <FormItem>
                        <FormLabel className="text-xs text-gray-400">When Value Is</FormLabel>
                        {options.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                                {[...options, ...unknownSelected.map((value) => ({ value, title: value }))].map(
                                    ({ value, title }) => {
                                        const id = `preselect-${ruleIndex}-${value}`;
                                        return (
                                            <div key={value} className="flex flex-row items-center gap-2">
                                                <Checkbox
                                                    id={id}
                                                    className="bg-white"
                                                    checked={selected.includes(value)}
                                                    onCheckedChange={(next) =>
                                                        field.onChange(
                                                            next
                                                                ? [...selected, value]
                                                                : selected.filter((v) => v !== value),
                                                        )
                                                    }
                                                />
                                                <Label htmlFor={id} className="text-sm font-normal">
                                                    {title}
                                                </Label>
                                            </div>
                                        );
                                    },
                                )}
                            </div>
                        ) : (
                            <FormControl>
                                <CommaSeparatedInput values={selected} onCommit={field.onChange} />
                            </FormControl>
                        )}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

/** Edits a list as comma-separated text, committing on blur so spaces and trailing commas can be typed. */
const CommaSeparatedInput: FC<
    Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
        values: string[];
        onCommit: (values: string[]) => void;
    }
> = ({ values, onCommit, ...rest }) => {
    const [draft, setDraft] = useState(values.join(', '));
    return (
        <Input
            {...rest}
            className="bg-white"
            placeholder="value-a, value-b"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() =>
                onCommit(
                    draft
                        .split(',')
                        .map((v) => v.trim())
                        .filter((v) => !!v),
                )
            }
        />
    );
};
